/* eslint-disable max-len */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useFormularioSecretariaMunicipalAgriculturaStore } from 'stores/forms/formularioAgricultura.store';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';

const INSTITUICOES = [
  { key: 'cooperativas', label: 'Cooperativas' },
  { key: 'embrapa', label: 'Embrapa' },
  { key: 'empresas_privadas', label: 'Empresas Privadas' },
  { key: 'ongs', label: 'ONGs' },
  { key: 'senar', label: 'Senar' },
  { key: 'universidades_publicas', label: 'Universidades Públicas' },
  { key: 'empaer', label: 'Empaer' },
  { key: 'ifmt', label: 'IFMT' },
  { key: 'incra', label: 'Incra' },
  { key: 'sebrae', label: 'Sebrae' },
  { key: 'universidades_privadas', label: 'Universidades Privadas' },
  { key: 'nenhuma', label: 'Nenhuma' },
];

const getInstitutionLabel = (name: string) => {
  const key = name.trim().toLowerCase();
  return INSTITUICOES.find(instituicao => instituicao.key === key)?.label ?? name;
};

const getImportanceColor = (index: number) => {
  const colors = ['#d32f2f', '#f57c00', '#fbc02d', '#689f38', '#1976d2'];
  return colors[index] || '#757575';
};

const getImportanceLabel = (index: number) => {
  const labels = ['1º lugar', '2º lugar', '3º lugar', '4º lugar', '5º lugar'];
  return labels[index] || `${index + 1}º lugar`;
};

const Step7View: React.FC = () => {
  const { formulario } = useFormularioSecretariaMunicipalAgriculturaStore();
  const [openProgramas, setOpenProgramas] = useState<boolean>(true);
  const [openAssistencia, setOpenAssistencia] = useState<boolean>(true);

  const programas = formulario?.programas_assistencia_tecnica || [];

  if (!formulario) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography>Carregando informações...</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* SEÇÃO 1: Programas desenvolvidos */}
      <Paper elevation={1} sx={{ p: 0, mt: 2 }}>
        <Box
          sx={{ px: 3, pt: 2, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setOpenProgramas(!openProgramas)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                color: "text.primary",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {openProgramas ? (
                <KeyboardArrowUpOutlined
                  sx={{ color: "text.secondary", fontSize: 20 }}
                />
              ) : (
                <KeyboardArrowDownOutlined
                  sx={{ color: "text.secondary", fontSize: 20 }}
                />
              )}
              Registros Cadastrados ({programas.length} registro{programas.length !== 1 ? 's' : ''})
            </Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />
        </Box>

        {openProgramas && (
          <Box sx={{ p: 3 }}>
            {programas.length === 0 ? (
              <Alert severity="info">
                <Typography variant="body2">
                  Nenhum programa foi cadastrado para este município.
                </Typography>
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Programa</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Parceiro</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Beneficiados</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tipo da Comunidade</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {programas.map((prog, index) => (
                      <TableRow key={prog.id || index}>
                        <TableCell>{prog.programa}</TableCell>
                        <TableCell>{prog.parceiros.join(', ')}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{prog.beneficiados}</TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>{prog.tipos_comunidade.join(', ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* SEÇÃO 2: Assistência Técnica - oculta conforme solicitado */}
      <Paper elevation={1} sx={{ p: 0, mt: 2, display: 'none' }}>
        <Box
          sx={{ px: 3, pt: 2, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setOpenAssistencia(!openAssistencia)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                color: "text.primary",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {openAssistencia ? (
                <KeyboardArrowUpOutlined
                  sx={{ color: "text.secondary", fontSize: 20 }}
                />
              ) : (
                <KeyboardArrowDownOutlined
                  sx={{ color: "text.secondary", fontSize: 20 }}
                />
              )}
              Assistência Técnica
            </Typography>
          </Box>
          <Divider sx={{ mb: 1 }} />
        </Box>

        {openAssistencia && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body1" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
                  Ordem de importância da assistência técnica prestada por outras instituições no município:
                </Typography>
              </Grid>

              <Grid item xs={12}>
                {formulario.ordem_importancia_assistencia_tecnica_outras_instituicoes &&
                  formulario.ordem_importancia_assistencia_tecnica_outras_instituicoes.length > 0 ? (
                  <Paper variant="outlined" sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                      Ranking de Importância
                    </Typography>

                    <List>
                      {formulario.ordem_importancia_assistencia_tecnica_outras_instituicoes.map((instituicao, index) => (
                        <ListItem
                          key={index}
                          sx={{
                            mb: 1,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={2}>
                                <Chip
                                  label={getImportanceLabel(index)}
                                  sx={{
                                    backgroundColor: getImportanceColor(index),
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                  size="small"
                                />
                                <Typography variant="body1" fontWeight="500">
                                  {getInstitutionLabel(instituicao)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Box sx={{ mt: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        Total de instituições ranqueadas: {formulario.ordem_importancia_assistencia_tecnica_outras_instituicoes.length}
                      </Typography>
                    </Box>
                  </Paper>
                ) : (
                  <Alert severity="info">
                    <Typography variant="body2">
                      Nenhuma ordem de importância da assistência técnica foi informada para este município.
                    </Typography>
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </>
  );
};

export default Step7View;
